import bb from 'billboard.js/dist/billboard.pkgd';
import { htmlDecode, htmlEncode } from '../../utils/escape.utils';
import { TemplateState } from '../../types/common/types';
import {
  GetTopThreatsBlockedResponse,
  Status,
  ThreatByDate,
  ThreatWithDelta,
} from '@nirvana/shared/dtos/types/widgets/common';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { getScreenWidth } from '../../utils/screen.utils';
import { TIME_RANGE } from '@nirvana/shared/constants';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { CommonThreatsNavigationHelper, NavigationItem, TopThreatsNavigationHelper } from '@nirvana/shared/navigation';
import { ThreatType } from '@nirvana/shared/dtos/types/shared/threats';
import { BaseThreatWidgetView } from '../../widget/BaseThreatWidgetView';

export class TopThreatsView extends BaseThreatWidgetView {
  private static readonly LONG_DATE_FORMAT_OPTIONS = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  private static readonly LONG_DATE_FORMAT_OPTIONS_WITH_HOUR = {
    month: 'long',
    day: 'numeric',
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  };
  private static readonly SHORT_DATE_FORMAT_OPTIONS = {
    month: 'short',
    day: 'numeric',
  };
  private static readonly SHORT_DATE_FORMAT_OPTIONS_WITH_HOUR = {
    month: 'short',
    day: 'numeric',
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  };
  private static readonly TOP_N = 5;
  private static readonly TICK_ROTATE_WIDTH_LIMIT = 1620;
  private refreshWidthFn = this.updateChartLabelsWidth.bind(this);
  public latestData: any;
  public shadow: any;
  protected translations: any;
  private $chart: any;
  private $chartContainer: HTMLElement;
  private $threatChartTooltip: HTMLElement;
  private $listContainer: HTMLElement;
  private errorColor: string;
  private $style: any;
  private latestTimeRange: number;
  private latestErrorChart: boolean;

  getDomElements() {
    super.getDomElements();
    this.$chartContainer = this.shadow.getElementById('topThreatChart');
    this.$threatChartTooltip = this.shadow.getElementById('threatChartTooltip');
    this.$listContainer = this.shadow.getElementById('topThreatList');
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#topThreatTitle');
    this.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
    window.addEventListener('resize', this.refreshWidthFn);
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getTopThreatsWidgetStrings();
  }

  setTranslatedStrings() {
    this.dom.$widgetTitleString.innerHTML = this.translations.title;
  }

  private mapChartColorsToThreatNames(uniqueNames: string[]) {
    const color1 = this.$style.getPropertyValue('--red-chart-600');
    const color2 = this.$style.getPropertyValue('--orange-alert-chart-100');
    const color3 = this.$style.getPropertyValue('--purple-chart-050');
    const color4 = this.$style.getPropertyValue('--green-layout-101-primary-green');
    const color5 = this.$style.getPropertyValue('--blue-chart-050');

    const lineColors = [color1, color2, color3, color4, color5];

    /* we need to trim the whitespace before the color value because it will
     break the color in the tooltip template
     */
    for (let i = 0; i < lineColors.length; i++) {
      lineColors[i] = lineColors[i].trimLeft();
    }
    return this.threatNamesToColors(uniqueNames, lineColors);
  }

  buildSuccessView(response: BackendResponseWithMeta<GetTopThreatsBlockedResponse>) {
    this.resetTemplateState(this.$wrapper);

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      this.latestData = result.data;
      const uniqThreatNames = this.getThreatNames(this.latestData.threatsByDate);
      const colorMap = this.mapChartColorsToThreatNames(uniqThreatNames);

      this.buildList(this.latestData.threats, colorMap);
      this.drawAreaChart(
        this.latestData.threatsByDate,
        response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        true,
        false
      );
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate() {
    this.errorColor = this.$style.getPropertyValue('--chart-101-pressed-dividers').trimLeft();
    const colorMap = { ['...']: this.errorColor };
    const data: Array<any> = [];

    for (let i = 0; i < TopThreatsView.TOP_N; i++) {
      data.push({
        delta: 0,
        threat: {
          count: 0,
          name: '...',
        },
      });
    }

    const graphData = [
      {
        date: '2020-05-12',
        threat: {
          count: 107,
        },
      },
      {
        date: '2020-05-13',
        threat: {
          count: 107,
        },
      },
      {
        date: '2020-05-14',
        threat: {
          count: 107,
        },
      },
      {
        date: '2020-05-15',
        threat: {
          count: 107,
        },
      },
      {
        date: '2020-05-16',
        threat: {
          count: 107,
        },
      },
      {
        date: '2020-05-17',
        threat: {
          count: 107,
        },
      },
      {
        date: '2020-05-18',
        threat: {
          count: 107,
        },
      },
    ];

    this.buildList(data, colorMap);
    this.drawAreaChart(graphData, 7, false, true);
  }

  private buildList(data: ThreatWithDelta[], colorMap: Object) {
    data.forEach((threat, index) => {
      this.buildListElement(threat, index, colorMap[threat.threat.name]);
    });
  }

  private buildListElement(threat: ThreatWithDelta, index: number, color: string) {
    const $listElement = this.$listContainer.children[index];

    const $threatName = $listElement.querySelector(`[gz-list-element="threatName"]`);
    // @ts-ignore
    $threatName.textContent = htmlDecode(threat.threat.name);
    const $threatValue = $listElement.querySelector(`[gz-list-element="threatValue"]`);
    // @ts-ignore
    $threatValue.textContent = threat.threat.count;
    const $threatDelta = $listElement.querySelector(`[gz-list-element="threatDelta"]`);

    const $threatColor = $listElement.querySelector(`[gz-list-element="threatColor"]`) as HTMLElement;
    $threatColor.style.backgroundColor = color;

    this.displayDeltaInElement($threatDelta, threat.delta, false);
  }

  private updateChartLabelsWidth() {
    if (!this.$chart || this.latestErrorChart || this.latestTimeRange !== TIME_RANGE.LAST_24_HOURS) {
      return;
    }

    // https://naver.github.io/billboard.js/release/latest/doc/Chart.html#config

    if (getScreenWidth() < TopThreatsView.TICK_ROTATE_WIDTH_LIMIT) {
      this.$chart.config('axis.x.tick.width', 50, true);
    } else {
      // This resets the enforced limit on width and makes the tick texts display on a single line
      this.$chart.config('axis.x.tick.width', null, true);
    }
  }

  private drawAreaChart(data, timeRange: number, interactionEnabled: boolean, errorChart: boolean) {
    const chartType = 'area-spline';
    let displayLinesCounter: number;

    const uniqThreatNames = this.getThreatNames(data);
    const userLanguage = htmlEncode(this.dependencies.getUserLanguage());
    const chartErrorClass = 'bb-chart-hidden-lines';

    const dateOptions =
      timeRange === TIME_RANGE.LAST_24_HOURS
        ? TopThreatsView.SHORT_DATE_FORMAT_OPTIONS_WITH_HOUR
        : TopThreatsView.SHORT_DATE_FORMAT_OPTIONS;

    const tooltipFormatOptions =
      timeRange === TIME_RANGE.LAST_24_HOURS
        ? TopThreatsView.LONG_DATE_FORMAT_OPTIONS_WITH_HOUR
        : TopThreatsView.LONG_DATE_FORMAT_OPTIONS;

    switch (timeRange) {
      case 1:
        displayLinesCounter = 4;
        break;
      case 7:
        displayLinesCounter = 1;
        break;
      case 30:
        displayLinesCounter = 5;
        break;
      default:
        displayLinesCounter = 1;
    }

    const gridLinesObjArray = this.getLinesObjectArray(data, displayLinesCounter);
    const datesForTicks = gridLinesObjArray.map(customDateObj => new Date(customDateObj.value));

    const tickFormatFn =
      timeRange === TIME_RANGE.LAST_24_HOURS
        ? this.getFormattedTickDate.bind(this, userLanguage, dateOptions, datesForTicks)
        : this.getFormattedDate.bind(this, userLanguage, dateOptions);

    // set in order to display x axis data points according to
    // https://jira.bitdefender.biz/browse/CERES-11877
    const maxCulling = timeRange === TIME_RANGE.LAST_7_DAYS ? 9 : 7;

    const optionsChart = {
      data: {
        x: 'date',
        // How the date is parsed.
        // See https://stackoverflow.com/questions/32642089/c3-js-timeseries-with-time-fails-to-parse for more details.
        xFormat: '%Y-%m-%d %H:%M',
        columns: [['date', ...this.getArrayOfDates(data)], ...this.formatThreatsByDateToArray(data)],
        types: {
          x: 'timeseries',
          ...this.threatNamesToLineType(uniqThreatNames, chartType),
        },
        colors: {
          ...this.mapChartColorsToThreatNames(uniqThreatNames),
        },
      },
      tooltip: {
        format: {
          title: this.getFormattedDate.bind(this, userLanguage, tooltipFormatOptions),
        },
        contents: {
          bindTo: this.$threatChartTooltip,
          template: `<div class="chart-tooltip-container"><div class="chart-tooltip-title">{=TITLE}</div>
                {{<div class='gz-data-item'>
                    <span class='gz-data-item--bullet' style=background-color:{=COLOR}></span>
                    <div class="gz-data-item--title">{=NAME}</div>
                    <div class="gz-data-item--value">{=VALUE}</div>
                </div>}}</div>`,
        },
      },
      axis: {
        x: {
          type: 'timeseries',
          height: 40,
          tick: {
            fit: true,
            culling: {
              max: maxCulling,
            },
            format: tickFormatFn,
          },
        },
        y: {
          tick: {
            culling: true,
          },
        },
      },
      grid: {
        x: {
          lines: gridLinesObjArray,
        },
        y: {
          show: true,
        },
      },
      legend: {
        show: false,
      },
      size: {
        height: 162,
      },
      point: {
        show: false,
      },
      interaction: {
        enabled: interactionEnabled,
      },
      bindto: this.$chartContainer,
    };

    // reset chart container class
    this.$chartContainer.classList.remove(chartErrorClass);
    /**
     * we need to destroy the chart, then generate it again if the options
     * are changed from one state to another
     * https://github.com/naver/billboard.js/issues/424
     */
    if (this.$chart) {
      this.$chart.destroy();
    }

    this.latestTimeRange = timeRange;
    this.latestErrorChart = errorChart;

    if (!errorChart) {
      this.$chart = bb.generate(optionsChart);
      this.updateChartLabelsWidth();
    } else {
      const optionsErrorChartFormatted = {
        ...optionsChart,
        axis: {
          x: {
            type: 'category',
            tick: {
              format(x) {
                return '--';
              },
            },
          },
          y: {
            max: 120,
            tick: {
              stepSize: 30,
            },
          },
        },
      };

      this.$chart = bb.generate(optionsErrorChartFormatted);
      if (!this.$chartContainer.classList.contains(chartErrorClass)) {
        this.$chartContainer.classList.add(chartErrorClass);
      }
    }
  }

  private getArrayOfDates(data: ThreatByDate[]) {
    const allDates = data.map(threatByDate => threatByDate.date);

    return this.getUniqueArray(allDates);
  }

  private getLinesObjectArray(data: ThreatByDate[], counter: number): Array<{ value: string }> {
    const allDates = this.getArrayOfDates(data).sort();
    const objectArray: Array<{ value: string }> = [];

    for (let i = 0; i < allDates.length; i += counter) {
      objectArray.push({ value: allDates[i] });
    }

    return objectArray;
  }

  private threatNamesToLineType(threatNames: Array<string>, type: string): Object {
    return threatNames.reduce((accumulator, threatName) => {
      return {
        ...accumulator,
        [threatName]: type,
      };
    }, {});
  }

  private threatNamesToColors(threatNames: Array<string>, colors: Array<string>): Object {
    return threatNames.reduce((accumulator, threatName, index) => {
      return {
        ...accumulator,
        [threatName]: colors[index],
      };
    }, {});
  }

  private formatThreatsByDateToArray(data: ThreatByDate[]): Array<any> {
    const uniqueThreatNames = this.getThreatNames(data);

    //Returns an array of arrays of type [ "threatName", 4, 5, 2, etc...]
    return uniqueThreatNames.map(threatName => [threatName, ...this.getThreatCountsForName(data, threatName)]);
  }

  private getThreatNames(data: ThreatByDate[]): Array<string> {
    const allThreatNames = data.map(threatByDate => threatByDate.threat.name);
    return this.getUniqueArray(allThreatNames);
  }

  private getFormattedDate(locale: string, options, date: Date): string {
    return date.toLocaleString(locale.replace(/_/g, '-'), options);
  }

  private getFormattedTickDate(locale: string, options, datesForTicks: Array<Date>, date: Date): string {
    const tickDateIndex = datesForTicks.findIndex(dateForTick => dateForTick.getTime() === date.getTime());
    if (tickDateIndex !== -1 && !this.shouldTickDateBeLong(tickDateIndex, datesForTicks)) {
      const noDayAndMonthOptions = {
        hour: 'numeric',
        hour12: false,
        minute: 'numeric',
      };
      return this.getFormattedDate(locale, noDayAndMonthOptions, date);
    }

    return this.getFormattedDate(locale, options, date);
  }

  private shouldTickDateBeLong(index: number, datesForTicks: Array<Date>): boolean {
    return index === 0 || datesForTicks[index - 1].getDay() !== datesForTicks[index].getDay();
  }

  private getThreatCountsForName(data: ThreatByDate[], name: string): Array<number> {
    return data
      .filter(threatByDate => threatByDate.threat.name === name)
      .map(threatByDate => threatByDate.threat.count);
  }

  private getUniqueArray(array: Array<any>): Array<any> {
    //  Turns the array with duplicates into a Set object with unique elements
    const uniqueSet = new Set(array);

    //  Turns the Set back into an array object
    return [...uniqueSet];
  }

  protected getNavigationItemForTitle(targets: Array<Target>, timeRange: number): NavigationItem {
    const threatTypes = this.getIntThreatTypesForTitle();
    const isPartner = Application.Main.company.isPartner();
    const filters = TopThreatsNavigationHelper.getFiltersForNavigation({
      timeRange: timeRange,
      threatTypes: threatTypes,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = TopThreatsNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([this.dom.$widgetTitle], filters, displayedColumns);
  }

  private getIntThreatTypesForTitle(): Array<ThreatType> {
    return this.latestData.threats.map((threat: ThreatWithDelta) => threat.threat.threatType);
  }

  protected getNavigationItemsForWidgetBody(targets: Array<Target>, timeRange: number): Array<NavigationItem> {
    const navigationItems = [] as Array<NavigationItem>;

    this.latestData.threats.forEach((threat, listElementIndex) => {
      const navigationItem = this.buildNavigationItemForListElement(threat, targets, timeRange, listElementIndex);
      navigationItems.push(navigationItem);
    });

    return navigationItems;
  }

  private buildNavigationItemForListElement(
    threat: ThreatWithDelta,
    targets: Array<Target>,
    timeRange: number,
    elementIndex: number
  ): NavigationItem {
    const $listElem = this.$listContainer.children[elementIndex].querySelector('.item-details');
    const threatTypes = [threat.threat.threatType];
    const isPartner = Application.Main.company.isPartner();
    const filters = TopThreatsNavigationHelper.getFiltersForNavigation({
      timeRange: timeRange,
      threatTypes: threatTypes,
      targets: targets,
      isPartner: isPartner,
    });
    const columns = TopThreatsNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([$listElem as HTMLElement], filters, columns);
  }
}
